import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NDA_SECTIONS = [
  {
    title: "1. Confidentialite absolue",
    content: "Le testeur s'engage a garder strictement confidentiel l'ensemble des fonctionnalites, algorithmes, interfaces, resultats d'analyse et toute information technique relative a OBGH Territoire Pro. Aucune information ne peut etre divulguee a un tiers, que ce soit verbalement, par ecrit, ou par tout autre moyen.",
    critical: true,
  },
  {
    title: "2. Interdiction de captures et enregistrements",
    content: "Il est strictement interdit de prendre des captures d'ecran, d'enregistrer des videos, de copier du contenu visuel ou de reproduire de quelque maniere que ce soit les interfaces, resultats d'analyse ou donnees de l'application, sauf autorisation ecrite prealable de l'administrateur OBGH.",
    critical: true,
  },
  {
    title: "3. Interdiction de partage d'acces",
    content: "Les identifiants de connexion (email, mot de passe, code d'invitation) sont strictement personnels et non transferables. Le testeur s'engage a ne jamais partager, preter ou rendre accessible son compte a une tierce personne. Toute utilisation non autorisee sera tracee et le compte sera immediatement desactive.",
    critical: true,
  },
  {
    title: "4. Obligation de rapport",
    content: "Le testeur s'engage a rapporter tout bug, probleme technique ou suggestion d'amelioration directement a l'administrateur OBGH. Les rapports doivent etre transmis de maniere confidentielle et ne doivent pas etre publies sur des forums, reseaux sociaux ou tout autre espace public.",
    critical: false,
  },
  {
    title: "5. Revocation d'acces",
    content: "OBGH se reserve le droit de revoquer l'acces du testeur a tout moment, sans preavis ni justification. En cas de violation de la presente entente, l'acces sera immediatement et definitivement revoque.",
    critical: true,
  },
  {
    title: "6. Propriete intellectuelle",
    content: "Toutes les technologies, algorithmes, interfaces et donnees de OBGH Territoire Pro sont la propriete exclusive de OBGH. Le testeur ne peut en aucun cas utiliser les informations obtenues pour developper, ameliorer ou contribuer a un produit concurrent.",
    critical: true,
  },
  {
    title: "7. Promotion et communication",
    content: "Le testeur s'engage a ne publier aucun avis, commentaire, critique ou promotion de l'application sans autorisation ecrite prealable de l'administrateur OBGH. Le droit de promouvoir sera accorde par ecrit lorsque l'administrateur le jugera opportun.",
    critical: true,
  },
  {
    title: "8. Duree de l'entente",
    content: "La presente entente de non-divulgation est en vigueur des l'acceptation par le testeur et demeure valide indefiniment, meme apres la fin de la periode de test ou la revocation de l'acces. Les obligations de confidentialite survivent a la fin de l'entente.",
    critical: false,
  },
];

const CHECKBOXES = [
  { key: 'confidential', text: "Je m'engage a garder strictement confidentiel tout ce que je verrai et utiliserai dans OBGH Territoire Pro." },
  { key: 'nocapture', text: "Je m'engage a ne prendre aucune capture d'ecran, video ou copie sans autorisation ecrite." },
  { key: 'noshare', text: "Je m'engage a ne jamais partager mes identifiants ou mon acces avec quiconque." },
  { key: 'nopromo', text: "Je m'engage a ne publier aucun avis ou promotion sans autorisation ecrite de l'administrateur." },
  { key: 'accept', text: "J'ai lu, compris et accepte l'integralite de cette entente de non-divulgation." },
];

const NDAPage = () => {
  const navigate = useNavigate();
  const [checkboxes, setCheckboxes] = useState({
    confidential: false, nocapture: false, noshare: false, nopromo: false, accept: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const allChecked = Object.values(checkboxes).every(Boolean);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('obgh_token');
      const res = await fetch(`${API_URL}/api/admin/nda/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        sessionStorage.setItem('obgh_nda_accepted', 'true');
        toast.success('Entente de non-divulgation acceptee');
        navigate('/terms');
      } else {
        toast.error('Erreur lors de l\'acceptation');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-obgh-black text-obgh-text-primary font-body" data-testid="nda-page">
      <header className="bg-obgh-surface border-b border-white/10 sticky top-0 z-50 backdrop-blur-glass">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Lock className="w-6 h-6 text-red-500" />
          <h1 className="text-lg font-heading font-black text-red-500 uppercase tracking-tight">
            Entente de non-divulgation — CONFIDENTIEL
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-6 p-4 bg-red-900/40 border-2 border-red-500/60 rounded-sm" data-testid="nda-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-heading font-black text-red-400 uppercase tracking-wider mb-1">
                Document confidentiel — Lecture obligatoire
              </h2>
              <p className="text-xs text-red-200/80 leading-relaxed">
                En tant que testeur terrain selectionne pour OBGH Territoire Pro, vous devez lire et accepter
                cette entente de non-divulgation AVANT d'acceder a l'application. Toute violation entrainera
                la desactivation immediate de votre compte et des poursuites potentielles.
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[45vh] overflow-y-auto pr-2 space-y-4 scrollbar-thin scrollbar-thumb-zinc-700">
          {NDA_SECTIONS.map((section) => (
            <section key={section.title}
              className={`border rounded-sm p-5 ${section.critical ? 'bg-red-900/10 border-red-500/30' : 'bg-obgh-surface border-white/10'}`}>
              <h3 className={`text-sm font-heading font-bold uppercase tracking-wider mb-2 ${section.critical ? 'text-red-400' : 'text-white'}`}>
                {section.title}
              </h3>
              <p className="text-xs text-zinc-300 leading-relaxed">{section.content}</p>
            </section>
          ))}
        </div>

        <div className="mt-6 bg-obgh-surface border-2 border-red-500/40 rounded-sm p-5" data-testid="nda-acceptance">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-red-400" />
            <span className="text-sm font-heading font-black text-red-400 uppercase tracking-wider">Engagement du testeur</span>
          </div>
          <div className="space-y-3 mb-5">
            {CHECKBOXES.map(({ key, text }) => (
              <label key={key} className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={checkboxes[key]}
                  onChange={(e) => setCheckboxes(p => ({ ...p, [key]: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-red-500 rounded-sm" data-testid={`nda-checkbox-${key}`} />
                <span className="text-xs text-zinc-300 group-hover:text-white transition-colors">{text}</span>
              </label>
            ))}
          </div>
          <button onClick={handleAccept} disabled={!allChecked || submitting} data-testid="accept-nda-btn"
            className={`w-full py-3 rounded-sm font-heading font-black uppercase tracking-wider text-sm transition-all ${
              allChecked && !submitting ? 'bg-red-600 text-white hover:bg-red-500 cursor-pointer' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
            }`}>
            {submitting ? 'Enregistrement...' : allChecked ? (
              <span className="flex items-center justify-center gap-2"><CheckCircle2 size={16} /> J'ACCEPTE L'ENTENTE DE NON-DIVULGATION</span>
            ) : 'VEUILLEZ COCHER TOUTES LES CASES (5/5)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NDAPage;
